/**
 * Neonatal / pediatric cEEG + aEEG specialty pack (Epic C3 / Gap #12).
 */

export const NEONATAL_SPECIALTY_SCHEMA_VERSION = "neonatal-specialty-v1.0.0";

export type NeonatalAgeBand =
  | "preterm_lt32w"
  | "preterm_32_36w"
  | "term_37_42w"
  | "infant_0_3m"
  | "pediatric_3m_2y"
  | "pediatric_2y_plus";

export type NeonatalAlertPreset = {
  presetId: string;
  ageBand: NeonatalAgeBand;
  label: string;
  seizureSuspectThreshold: number;
  burstSuppressionAlert: boolean;
  discontinuousPatternAlert: boolean;
  audible: boolean;
};

export type NeonatalMontagePreset = {
  montageId: string;
  name: string;
  ageBands: NeonatalAgeBand[];
  channelHints: string[];
};

export const NEONATAL_INTENDED_USE =
  "Assistive neonatal/pediatric cEEG, aEEG visualization, and reduced-montage seizure-suspect detection for bedside navigation and documentation. Confirm candidates on raw EEG. Not a cleared point-of-care seizure alarm as sole diagnostic.";

export const NEONATAL_EXCLUDED_USE =
  "Excluded: autonomous POC seizure alarms as sole diagnostic, closed-loop therapy, silent adult AI triage models applied to neonates.";

export const NEONATAL_AGE_BAND_RULES: Array<{
  band: NeonatalAgeBand;
  label: string;
  notes: string;
}> = [
  {
    band: "preterm_lt32w",
    label: "Preterm <32 weeks PMA",
    notes: "Expect discontinuous tracé discontinu; cautious seizure labeling",
  },
  {
    band: "preterm_32_36w",
    label: "Preterm 32–36 weeks PMA",
    notes: "Increasing continuity; delta brushes may appear",
  },
  {
    band: "term_37_42w",
    label: "Term 37–42 weeks PMA",
    notes: "Near-continuous quiet sleep / active sleep cycling",
  },
  {
    band: "infant_0_3m",
    label: "Infant 0–3 months",
    notes: "Pediatric reduced montages; age-adjusted alerts",
  },
  {
    band: "pediatric_3m_2y",
    label: "Pediatric 3 months–2 years",
    notes: "Standard pediatric montage options",
  },
  {
    band: "pediatric_2y_plus",
    label: "Pediatric ≥2 years",
    notes: "Approach adult montages with pediatric filters",
  },
];

export const NEONATAL_MONTAGE_PRESETS: NeonatalMontagePreset[] = [
  {
    montageId: "neonatal-reduced",
    name: "Neonatal reduced",
    ageBands: ["preterm_lt32w", "preterm_32_36w", "term_37_42w"],
    channelHints: ["Fp1", "Fp2", "C3", "C4", "T3", "T4", "O1", "O2", "Cz"],
  },
  {
    montageId: "pediatric-double-banana",
    name: "Pediatric double banana",
    ageBands: ["infant_0_3m", "pediatric_3m_2y", "pediatric_2y_plus"],
    channelHints: ["Fp1-F3", "F3-C3", "C3-P3", "P3-O1"],
  },
];

export const NEONATAL_ALERT_PRESETS: NeonatalAlertPreset[] = [
  {
    presetId: "nicu-term-seizure-assist",
    ageBand: "term_37_42w",
    label: "Term NICU seizure-suspect assist",
    seizureSuspectThreshold: 0.7,
    burstSuppressionAlert: true,
    discontinuousPatternAlert: false,
    audible: true,
  },
  {
    presetId: "nicu-preterm-continuity",
    ageBand: "preterm_lt32w",
    label: "Preterm continuity / BS assist",
    seizureSuspectThreshold: 0.85,
    burstSuppressionAlert: true,
    discontinuousPatternAlert: true,
    audible: true,
  },
];

export type NeonatalReportTemplate = {
  templateId: "neonatal_ceeg";
  name: string;
  description: string;
  sections: string[];
  intendedUseLabel: string;
};

export const NEONATAL_REPORT_TEMPLATE: NeonatalReportTemplate = {
  templateId: "neonatal_ceeg",
  name: "Neonatal / NICU cEEG",
  description: "Neonatal continuous EEG with aEEG bedside correlation",
  sections: [
    "clinicalHistory",
    "gestationalAge",
    "recordingConditions",
    "aeegSummary",
    "eventsAndSeizures",
    "clinicalCorrelation",
  ],
  intendedUseLabel: NEONATAL_INTENDED_USE,
};

export type AeegBedsideViewModel = {
  studyId: string;
  ageBand: NeonatalAgeBand;
  aeegUpperUv: number[];
  aeegLowerUv: number[];
  timeSeconds: number[];
  seekToRawSeconds: number | null;
  alertPresetId: string | null;
  labeling: string;
};

export function buildAeegBedsideView(input: {
  studyId: string;
  ageBand: NeonatalAgeBand;
  aeegSamples: Array<{ timeSeconds: number; upperUv: number; lowerUv: number }>;
  cursorSeconds?: number;
  alertPresetId?: string | null;
}): AeegBedsideViewModel {
  const samples = input.aeegSamples;
  return {
    studyId: input.studyId,
    ageBand: input.ageBand,
    aeegUpperUv: samples.map((s) => s.upperUv),
    aeegLowerUv: samples.map((s) => s.lowerUv),
    timeSeconds: samples.map((s) => s.timeSeconds),
    seekToRawSeconds: input.cursorSeconds ?? samples[0]?.timeSeconds ?? null,
    alertPresetId: input.alertPresetId ?? null,
    labeling: NEONATAL_INTENDED_USE,
  };
}

export function resolveNeonatalAlertPreset(
  ageBand: NeonatalAgeBand,
): NeonatalAlertPreset | null {
  return (
    NEONATAL_ALERT_PRESETS.find((p) => p.ageBand === ageBand) ||
    NEONATAL_ALERT_PRESETS[0] ||
    null
  );
}

export function getNeonatalLabelingBundle() {
  return {
    intendedUse: NEONATAL_INTENDED_USE,
    excludedUse: NEONATAL_EXCLUDED_USE,
    nonOverclaimNote:
      "Use is limited to bedside NICU and pediatric EEG workflow support without claiming standalone seizure-detection clearance or FDA/CE diagnostic equivalence.",
    schemaVersion: NEONATAL_SPECIALTY_SCHEMA_VERSION,
  };
}
